/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { SVGIcon, ExtendedTooltip, Text, theme } from "@nordcloud/gnui";
import { CloudAccountStatus } from "~/tools";

type Props = {
  status: CloudAccountStatus;
  size?: keyof typeof theme.iconSize;
};

export function AccountStatusIcon({ status, size }: Props) {
  const { label, description, icon, color } = useMemo(
    () => accountStatusIconProps[status],
    [status]
  );

  return (
    <ExtendedTooltip
      status={color}
      hideTimeout={0}
      position="start"
      caption={
        <>
          <Text weight="bold" mb={0} size="sm" color={theme.color.text.text04}>
            {label}
          </Text>
          <Text mb={0} size="sm" color={theme.color.text.text04}>
            {description}
          </Text>
        </>
      }
    >
      <SVGIcon
        color={color}
        name={icon}
        css={{ verticalAlign: "sub" }}
        size={size}
      />
    </ExtendedTooltip>
  );
}

export const accountStatusIconProps = {
  [CloudAccountStatus.NoAccess]: {
    label: "No Access",
    description: "No access to the account",
    status: CloudAccountStatus.NoAccess,
    icon: "cloudOffline" as const,
    color: "danger" as const,
  },
  [CloudAccountStatus.NoData]: {
    label: "No Data",
    description: "No data has been received recently",
    status: CloudAccountStatus.NoData,
    icon: "cloudQuestionMark" as const,
    color: undefined,
  },
  [CloudAccountStatus.BillingOnly]: {
    label: "Limited access",
    description: "Only basic billing data access",
    status: CloudAccountStatus.BillingOnly,
    icon: "cloudWithDollar" as const,
    color: "warning" as const,
  },
  [CloudAccountStatus.ApiOnly]: {
    label: "Limited access",
    description: "Access to Cloud Estate API, but no billing data",
    status: CloudAccountStatus.ApiOnly,
    icon: "cloudDataOnly" as const,
    color: "warning" as const,
  },
  [CloudAccountStatus.FullAccess]: {
    label: "Full access",
    description: "Complete account access",
    status: CloudAccountStatus.FullAccess,
    icon: "cloudOnline" as const,
    color: "success" as const,
  },
};
